import { useState } from 'react';
import { Receipt, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '@/api/client';

interface Props {
  orderId: number | string;
  systemOrderNo?: string;
  variant?: 'primary' | 'ghost';
  /**
   * `none` emits no padding/height of its own, for callers that impose a shared button shape via
   * `className` (the order-detail header, where every action must be the same height). Without it
   * the built-in `px-3 py-1.5` and the caller's sizing are two competing padding utilities and which
   * one wins depends on stylesheet order, not on the order they're listed in.
   */
  size?: 'sm' | 'md' | 'none';
  className?: string;
}

export function MoneyReceiptButton({ orderId, systemOrderNo, variant = 'ghost', size = 'sm', className = '' }: Props) {
  const [busy, setBusy] = useState(false);

  const handle = async () => {
    setBusy(true);
    try {
      // Get the JWT, then fire a window.open to backend endpoint with token via fetch fallback
      const token = localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken');
      if (!token) { toast.error('Please log in again'); setBusy(false); return; }
      // Backend returns HTML which the browser renders + auto-prints if ?print=1
      // We fetch the HTML, open a blob window so the user can review then print
      const r = await api.get(`/orders/${orderId}/money-receipt/html`, { responseType: 'text' });
      const blob = new Blob([r.data as any], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank');
      if (!w) toast.error('Pop-up blocked — allow pop-ups for this site');
      else toast.success(`Money receipt for ${systemOrderNo ?? '#' + orderId} opened`);
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Failed to generate receipt');
    } finally { setBusy(false); }
  };

  const base = 'inline-flex items-center gap-1.5 rounded-lg font-medium transition disabled:opacity-50';
  const sz = size === 'none' ? '' : size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-sm';
  const va = variant === 'primary'
    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
    : 'bg-indigo-50 text-indigo-700 hover:bg-indigo-100 border border-indigo-200';

  return (
    <button onClick={handle} disabled={busy} className={`${base} ${sz} ${va} ${className}`}>
      {busy ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />}
      Money Receipt
    </button>
  );
}
